<template>
  <div class="bh-text-area--wrap">
    <label :for="id" v-if="label" class="bh-text-area__label">{{
      label
    }}</label>
    <div class="bh-text-area">
      <div class="bh-text-area-icons" v-if="showConfirmEdit">
        <v-icon
          class="bh-text-area__icon bh-text-area__icon--editing"
          v-on:click="toggleEdit"
          v-if="!isEditing"
          color="accent"
        >
          bhi-pencil
        </v-icon>
        <div v-else>
          <v-icon
            class="bh-text-input__icon bh-text-input__icon--save"
            :class="error ? 'invalid' : ''"
            v-on:click="updateValue($refs['input'].value)"
            color="accent"
          >
            bhi-check-circle-filled
          </v-icon>
          <v-icon
            class="bh-text-input__icon bh-text-input__icon--cancel"
            v-on:click="cleanUpValues"
            color="accent"
          >
            bhi-close-circle
          </v-icon>
        </div>
      </div>

      <textarea
        type="text"
        class="bh-text-area__input"
        ref="input"
        :class="[roundedClass, { resizeable: resize }]"
        :placeholder="placeholder"
        :id="id"
        :value="showConfirmEdit && isEditing ? newValue : value"
        :readonly="isReadonly"
        v-on:keyup="
          !isReadonly &&
            isEditing &&
            validateMethod($event.target.value, validationType)
        "
        @blur="onBlur"
        @input="onInput"
      />
      <div class="d-flex">
        <div class="bh-text-area__rules flex-1 pr-4">
          <template v-if="isRuleInvalid">
            {{ rulesError }}
          </template>
        </div>
        <span
          class="bh-text-area__counter"
          v-if="showCounter"
          :class="currentCount && currentCount > counter ? 'tooLong' : ''"
          >{{ currentCount }}/{{ counter }}</span
        >
      </div>

      <v-tooltip v-model="error" top>
        <template v-slot:activator="{ attrs }">
          <span class="" v-bind="attrs"></span>
        </template>
        <span>Too many characters</span>
      </v-tooltip>
    </div>
  </div>
</template>
<script>
export default {
  name: 'BhTextArea',
  props: {
    value: {
      type: String,
      default: '',
    },
    roundedClass: {
      type: String,
      default: 'rounded-sm',
    },
    id: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    resize: {
      type: Boolean,
      default: true,
    },
    counter: {
      type: [Boolean, Number],
      default: false,
    },
    showCounterInRange: {
      type: [Number],
      default: 0,
    },
    showConfirmEdit: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Boolean,
      default: false,
    },
    validationType: {
      type: String,
      default: 'required',
    },
    rules: {
      type: Array,
      default: () => [],
    },
    forceRules: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    newValue: '',
    countValue: '',
    isEditing: false,
    error: false,
    inputChanged: false,
  }),
  methods: {
    onBlur() {
      if (!this.isReadonly) {
        this.inputChanged = true
      }
    },
    onInput($event) {
      this.inputChanged = true
      this.count($event.target.value)
      this.$emit('input-text', $event.target.value)
    },
    toggleEdit() {
      this.newValue = this.value
      this.isEditing = !this.isEditing
      this.error = false
      this.focusInput()
    },
    updateValue(value) {
      if (this.error) return

      this.isEditing = !this.isEditing
      let newValue = value

      this.$emit('input', newValue)
      if (this.showConfirmEdit) {
        this.newValue = newValue
      }
    },
    count(val) {
      this.newValue = val
      this.countValue = val
    },
    validateMethod(value) {
      if (this.validate) {
        this.error = false
        this.newValue = value
        if (this.validationType == 'maxlength') {
          if (this.newValue.length > this.counter) this.error = true
        }
      }
    },
    cleanUpValues() {
      this.isEditing = !this.isEditing
      this.error = false
      this.countValue = this.value
      this.newValue = this.value
    },
    focusInput() {
      this.$refs.input.focus()
    },
  },
  watch: {
    value(val) {
      this.newValue = val
    },
    newValue(val) {
      this.countValue = val
    },
  },
  mounted() {
    this.newValue = this.value
    this.countValue = this.value
  },
  computed: {
    currentCount() {
      return this.countValue ? this.countValue.length : 0
    },
    isReadonly() {
      const { readonly, showConfirmEdit, isEditing } = this

      return readonly || (showConfirmEdit && !isEditing)
    },
    showCounter() {
      return (
        this.counter &&
        (this.showCounterInRange === 0 ||
          this.counter - this.showCounterInRange <= this.currentCount)
      )
    },
    rulesError() {
      if (!this.rules || this.readonly || this.disabled) return false

      const currentValue =
        this.showConfirmEdit && this.isEditing ? this.newValue : this.value

      for (const rule of this.rules) {
        const ruleValid = rule(currentValue)
        if (ruleValid !== true) {
          return ruleValid
        }
      }

      return ''
    },
    isRuleInvalid() {
      return (
        this.rules &&
        this.rules.length > 0 &&
        this.rulesError &&
        (this.forceRules || this.inputChanged)
      )
    },
  },
}
</script>
<style lang="scss">
.bh-text-area {
  width: 100%;
  min-height: 88px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;

  &--wrap {
    position: relative;
  }
  &__label {
    color: $bh-black;
    font-weight: 700;
    font-size: 0.857rem;
    line-height: 1.6667;
  }

  &__input {
    flex: 1;
    padding: 10px 12px;
    min-width: 0;
    min-height: 88px;
    font-weight: 400;
    line-height: 1.43;
    background: $bh-gray--6;
    resize: none;
    outline: none;
    &.resizeable {
      resize: vertical;
    }
  }

  &__rules {
    font-size: 0.8em;
    color: var(--bh-red--dark);
  }

  &__counter {
    font-size: 0.8em;
    align-self: flex-end;
    color: $bh-gray;
    &.tooLong {
      color: $bh-red--dark;
    }
  }
  &-icons {
    position: absolute !important;
    right: 0;
    top: 0;
  }
  &__icon--editing {
    margin-right: 8px;
  }
  .bh-text-input__icon {
    &::after {
      display: none;
    }
    &.invalid {
      opacity: 0.5 !important;
      cursor: default;
    }
  }
}
</style>
